import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import DataEditor from '@glideapps/glide-data-grid'
import '@glideapps/glide-data-grid/dist/index.css'
import { WORKER_URL } from '../config'
import { classifyColumns } from '../lib/classifyColumns'
import { useGridState } from '../lib/useGridState'
import { useGridData } from '../hooks/useGridData'
import MacroCharts from '../components/MacroCharts'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { gridTheme } from '../components/grid/theme'

type Status = 'loading' | 'processing' | 'ready'

export default function View() {
  const { id } = useParams<{ id: string }>()

  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight })

  const [status, setStatus] = useState<Status>('loading')
  const [error, setError] = useState<string | null>(null)
  const [timestamp, setTimestamp] = useState<string | null>(null)
  const [rowCount, setRowCount] = useState(0)

  const { sortRules, freezeCount, colWidths, colOrder, toggleSort, setColWidth, setColOrder } =
    useGridState(id ?? 'view')

  const fetchPage = useCallback(
    async (offset: number, limit: number) => {
      if (!id) throw new Error('Missing id')
      const r = await fetch(`${WORKER_URL}/data/${id}/rows?offset=${offset}&limit=${limit}`)
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json() as Promise<{ rows: Record<string, unknown>[]; total: number }>
    },
    [id],
  )

  const grid = useGridData({
    totalRows: rowCount,
    fetchPage,
    sortRules,
    colOrder,
    colWidths,
  })

  const chartSpecs = useMemo(
    () => (grid.fullyLoaded ? classifyColumns(grid.schema, grid.rows) : []),
    [grid.schema, grid.rows, grid.fullyLoaded],
  )

  const checkMeta = useCallback(() => {
    if (!id) return
    fetch(`${WORKER_URL}/data/${id}/meta`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json() as Promise<{ id: string; status: string; timestamp: string; rowCount: number }>
      })
      .then(({ status: s, timestamp: ts, rowCount: rc }) => {
        setTimestamp(ts)
        if (s === 'processing') {
          setStatus('processing')
        } else if (s === 'ready') {
          setStatus('ready')
          setRowCount(rc)
        } else {
          setError(`Unexpected status: ${s}`)
        }
      })
      .catch((e: Error) => setError(e.message))
  }, [id])

  useEffect(() => { checkMeta() }, [checkMeta])

  useEffect(() => {
    if (status !== 'processing') return
    const timer = setInterval(checkMeta, 2000)
    return () => clearInterval(timer)
  }, [status, checkMeta])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => setSize({ width: el.offsetWidth, height: el.offsetHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  if (!id) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', inset: 0, color: '#555' }}>
        No dashboard ID provided.
      </div>
    )
  }

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', inset: 0, color: '#666' }}>
        Loading…
      </div>
    )
  }

  if (status === 'processing') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', inset: 0, flexDirection: 'column', gap: 12 }}>
        <div style={{ color: '#0af', fontSize: 13 }}>Processing data…</div>
        <div style={{ color: '#555', fontSize: 11 }}>This usually takes a few seconds. Checking automatically.</div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', inset: 0, flexDirection: 'column', gap: 8 }}>
        <span style={{ color: '#f55', fontSize: 14 }}>Failed to load</span>
        <code style={{ color: '#888', fontSize: 12 }}>{error}</code>
      </div>
    )
  }

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 20px', borderBottom: '1px solid #1a1a1a', flexShrink: 0 }}>
        <span style={{ fontWeight: 800, fontSize: 16, letterSpacing: -0.5 }}>
          insta<span style={{ color: '#0af' }}>dash</span>
        </span>
        <code style={{ color: '#333', fontSize: 11, fontFamily: 'monospace' }}>{id}</code>
        {timestamp && (
          <span style={{ marginLeft: 'auto', color: '#444', fontSize: 11 }}>
            {new Date(timestamp).toLocaleString()}
          </span>
        )}
      </div>

      {/* Macro charts hidden until launch — beta feature, re-enable post-launch */}
      {/* <MacroCharts specs={chartSpecs} rows={grid.rows} /> */}

      <div ref={containerRef} style={{ flex: 1, overflow: 'hidden' }}>
        {size.width > 0 && grid.schema.length > 0 && (
          <ErrorBoundary>
            <DataEditor
              ref={grid.gridRef}
              getCellContent={grid.getCellContent}
              columns={grid.columns}
              rows={grid.rowCount}
              width={size.width}
              height={size.height}
              rowMarkers="number"
              smoothScrollX
              smoothScrollY
              theme={gridTheme}
              freezeColumns={freezeCount}
              onVisibleRegionChanged={grid.onVisibleRegionChanged}
              onHeaderClicked={(col, event) => {
                if (!grid.fullyLoaded) return
                const colId = grid.columns[col]?.id
                if (colId) toggleSort(colId, (event as unknown as MouseEvent).shiftKey)
              }}
              onColumnResize={(_col, newSize, colIndex) => {
                const colId = grid.columns[colIndex]?.id
                if (colId) setColWidth(colId, newSize)
              }}
              onColumnMoved={(startIndex, endIndex) => {
                const ids = grid.columns.map(c => c.id ?? '')
                const reordered = [...ids]
                const [removed] = reordered.splice(startIndex, 1)
                reordered.splice(endIndex, 0, removed)
                setColOrder(reordered)
              }}
            />
          </ErrorBoundary>
        )}
      </div>

      <div style={{ display: 'flex', gap: 16, padding: '6px 20px', borderTop: '1px solid #1a1a1a', fontSize: 11, color: '#333', flexShrink: 0, alignItems: 'center' }}>
        <span>{rowCount.toLocaleString()} row{rowCount !== 1 ? 's' : ''}</span>
        <span>·</span>
        <span>{grid.columns.length} column{grid.columns.length !== 1 ? 's' : ''}</span>
        {!grid.fullyLoaded && (
          <>
            <span>·</span>
            <span style={{ color: '#0af' }}>loading {grid.loadPct}%</span>
          </>
        )}
        {grid.fullyLoaded && sortRules.length > 0 && (
          <>
            <span>·</span>
            <span style={{ color: '#4ade80' }}>
              sorted by {sortRules.map(r => `${r.col} ${r.dir}`).join(', ')}
            </span>
          </>
        )}
        {freezeCount > 0 && (
          <>
            <span>·</span>
            <span style={{ color: '#0af' }}>{freezeCount} frozen</span>
          </>
        )}
      </div>
    </div>
  )
}
